import { REPORT_SECTION_TITLE } from '@/pages/CompanyReport/constants';
import type { CompanyReportSection } from '@/pages/CompanyReport/useSection';
import { SUPPORTED_LOCALES, type Locale } from '@/utils/locale';
import { SWS_HOST } from '@/constants';

export const getPartOf = (section: CompanyReportSection, url: string) => {
  const partOf = [
    `${url}`,
    `${url}/valuation`,
    `${url}/future`,
    `${url}/past`,
    `${url}/health`,
    `${url}/dividend`,
    `${url}/management`,
    `${url}/ownership`,
    `${url}/information`,
  ];
  return section === 'summary'
    ? partOf.slice(1, partOf.length)
    : partOf.filter((webPage) => !webPage.endsWith(section));
};

type GetSectionSchema = {
  section: CompanyReportSection;
  companyName: string;
  url: string;
  title: string;
  description: string;
};
export const getSectionSchema = ({
  section,
  companyName,
  url,
  title,
  description,
}: GetSectionSchema) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    specialty: 'Teaching retail investors how to research stocks',
    mainEntity: {
      '@type': 'CreativeWork',
      about: `The page contains information regarding the ${REPORT_SECTION_TITLE[section]} of ${companyName}`,
      url,
      description,
      audience: 'Retail Investors',
      author: {
        '@type': 'Organization',
        name: 'Simply Wall St Pty Ltd.',
      },
      dateModified: '2023-08-25T00:00:00.000Z',
      headline: title,
      isAccessibleForFree: true,
      isPartOf: getPartOf(section, url),
    },
  };
};

const createHref = (
  canonicalURL: string,
  section: CompanyReportSection,
  locale: Locale
) =>
  `${SWS_HOST}${locale === 'en' ? '' : '/' + locale}${canonicalURL}${
    section === 'summary' ? '' : '/' + section
  }`;

export const getCanonical = createHref;

// Potentially a future issue, but supported locales used to create all alternates does not include `en` by default
const LOCALES = ['en' as const, ...SUPPORTED_LOCALES];

export const getAlternates = (
  canonicalURL: string,
  section: CompanyReportSection,
  locale: Locale
) => {
  const alternates: Array<{ lang: 'x-default' | Locale; href: string }> = [
    // Default alternate link should be the `en` locale, which is the base canonical URL.
    {
      lang: 'x-default',
      href: createHref(canonicalURL, section, 'en'),
    },
  ];

  for (const l of LOCALES) {
    if (l !== locale) {
      alternates.push({
        lang: l,
        href: createHref(canonicalURL, section, l),
      });
    }
  }

  return alternates;
};
